import { useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  CalendarCheck, Loader2, AlertCircle, ChevronLeft, QrCode,
  FileText, Package, Coffee, CheckCircle2, MapPin, Printer, CreditCard, Lock, Upload,
  Paperclip, Download,
} from "lucide-react";
import { BASE_URL } from "../../../features/api/apiSlice";
import { useUploadProfilePhotoMutation } from "../../auth/api/authApi";
import {
  useGetMyEventRegistrationQuery,
  useGetMyEventInvoiceQuery,
  useGetMyEventReceiptQuery,
  useGetMyEventAccessCardQuery,
  useGetEventDocumentsQuery,
  useInitiateEventPaymentMutation,
} from "../api/eventsApi";
import { submitOnePayForm } from "../../logbook/utils/onepay";
import { AccessCardView } from "../components/AccessCard";
import {
  attendanceDisplay,
  dayAttendanceDisplay,
  ATTENDANCE_TONE_CLASSES,
} from "../utils/attendance";
import { registrationFee } from "../utils/fee";

function fmt(d: string) {
  return new Date(d).toLocaleDateString("en-NG", { day: "numeric", month: "long", year: "numeric" });
}

// The only two statuses that admit a member: a settled fee, or an event that
// never charged one. Anything else — pending, failed, missing — is not paid.
const SETTLED_STATUSES = ["paid", "free"];
const isSettled = (reg: any) => SETTLED_STATUSES.includes(String(reg?.payment_status));

// GET /my/events/{id}/invoice names its fields after the registration row:
// amount_paid, payment_status, payment_ref, registered_at. Reading .amount /
// .status found nothing, so an outstanding fee rendered as "Free" with a blank
// status — and the receipt printed a hardcoded "paid". Read both spellings and
// fall back to the registration, which is the same source of truth.
function readInvoice(invoice: any, reg: any) {
  const status = invoice?.payment_status ?? invoice?.status ?? reg?.payment_status ?? null;
  const amount = invoice?.amount_paid ?? invoice?.amount ?? reg?.amount_paid ?? null;

  return {
    invoice_no: invoice?.invoice_no ?? reg?.invoice_no ?? null,
    amount: amount != null ? Number(amount) : null,
    status,
    paid: status === "paid",
    reference: invoice?.payment_ref ?? invoice?.payment_reference ?? reg?.payment_reference ?? null,
    issued_at: invoice?.registered_at ?? invoice?.issued_at ?? reg?.registered_at ?? null,
  };
}

const METHOD_LABELS: Record<string, string> = {
  onepay:        "Online (OnePay)",
  bank_transfer: "Bank Transfer",
  bank_deposit:  "Bank Deposit",
  cash:          "Cash",
  pos:           "POS",
};

// `receipt` is GET /my/events/{id}/receipt — issued only once the fee is
// confirmed, and carrying the receipt number stamped on the payment itself, so
// this print and the receipt emailed to the member name the same document.
function printReceipt(receipt: any, reg: any, event: any) {
  // A receipt is proof money was received. No confirmed payment, no receipt.
  if (!receipt || receipt.status !== "paid") return;
  const w = window.open("", "_blank", "width=720,height=940");
  if (!w) return;
  const amount   = receipt.amount != null ? `&#8358;${Number(receipt.amount).toLocaleString()}` : "—";
  const issueDate = receipt.paid_at ? new Date(receipt.paid_at).toLocaleDateString("en-NG", { day: "numeric", month: "long", year: "numeric" }) : "—";
  const method   = METHOD_LABELS[receipt.payment_method] ?? receipt.payment_method ?? "—";
  w.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>Receipt ${receipt.receipt_number ?? reg.registration_no ?? ""}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0;font-family:'Segoe UI',Arial,sans-serif}
    body{background:#f4f4f4;padding:40px}
    .r{width:600px;margin:0 auto;background:#fff;border-radius:18px;overflow:hidden;box-shadow:0 8px 32px rgba(0,0,0,.1)}
    .top{background:#111;color:#fff;padding:28px 32px;display:flex;justify-content:space-between;align-items:flex-start}
    .top .org{font-size:20px;font-weight:900}
    .top .sub{font-size:9px;font-weight:700;letter-spacing:.2em;text-transform:uppercase;color:#888;margin-top:5px}
    .top .inv-no{font-size:22px;font-weight:900;text-align:right}
    .top .inv-label{font-size:9px;font-weight:900;letter-spacing:.2em;text-transform:uppercase;color:#777;text-align:right;margin-bottom:4px}
    .body{padding:32px}
    .sec{font-size:9px;font-weight:900;letter-spacing:.2em;text-transform:uppercase;color:#bbb;margin-bottom:12px}
    .grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:24px}
    .f{background:#f8f8f8;border-radius:10px;padding:12px 14px}
    .fl{font-size:9px;font-weight:900;letter-spacing:.15em;text-transform:uppercase;color:#bbb;margin-bottom:3px}
    .fv{font-size:13px;font-weight:900;color:#111}
    .amt{background:#111;color:#fff;border-radius:14px;padding:22px 26px;display:flex;justify-content:space-between;align-items:center;margin-bottom:24px}
    .amt-label{font-size:9px;font-weight:900;letter-spacing:.2em;text-transform:uppercase;color:#888}
    .amt-val{font-size:30px;font-weight:900}
    .badge{display:inline-block;padding:5px 14px;border-radius:20px;font-size:9px;font-weight:900;letter-spacing:.15em;text-transform:uppercase;background:#dcfce7;color:#166534}
    .foot{border-top:1px solid #f0f0f0;padding:18px 32px;text-align:center}
    .foot span{font-size:9px;color:#ccc;font-weight:700;letter-spacing:.12em;text-transform:uppercase}
    @media print{body{background:#fff;padding:0}.r{box-shadow:none}}
  </style>
</head>
<body>
  <div class="r">
    <div class="top">
      <div>
        <div class="org">NIQS Nigeria</div>
        <div class="sub">Nigerian Institute of Quantity Surveyors</div>
      </div>
      <div>
        <div class="inv-label">Receipt No.</div>
        <div class="inv-no">${receipt.receipt_number ?? "—"}</div>
      </div>
    </div>
    <div class="body">
      <div class="sec">Event Details</div>
      <div class="grid">
        <div class="f" style="grid-column:span 2"><div class="fl">Event</div><div class="fv">${receipt.event?.title ?? event.title ?? "—"}</div></div>
        ${event.start_date ? `<div class="f"><div class="fl">Date</div><div class="fv">${new Date(event.start_date).toLocaleDateString("en-NG", { day: "numeric", month: "long", year: "numeric" })}</div></div>` : ""}
        ${event.location ? `<div class="f"><div class="fl">Venue</div><div class="fv">${event.location}</div></div>` : ""}
      </div>
      <div class="sec">Attendee Details</div>
      <div class="grid" style="margin-bottom:24px">
        <div class="f"><div class="fl">Name</div><div class="fv">${receipt.attendee_name ?? reg.attendee_name ?? "—"}</div></div>
        <div class="f"><div class="fl">Registration No.</div><div class="fv">${receipt.registration_no ?? reg.registration_no ?? "—"}</div></div>
        ${receipt.membership_no ? `<div class="f"><div class="fl">Membership No.</div><div class="fv">${receipt.membership_no}</div></div>` : ""}
        ${receipt.attendee_email ? `<div class="f"${receipt.membership_no ? "" : ` style="grid-column:span 2"`}><div class="fl">Email</div><div class="fv">${receipt.attendee_email}</div></div>` : ""}
      </div>
      <div class="amt">
        <div>
          <div class="amt-label">Amount Paid</div>
          <div class="amt-val">${amount}</div>
        </div>
        <span class="badge">Paid</span>
      </div>
      <div class="grid">
        <div class="f"><div class="fl">Payment Date</div><div class="fv">${issueDate}</div></div>
        <div class="f"><div class="fl">Method</div><div class="fv">${method}</div></div>
        ${receipt.reference ? `<div class="f" style="grid-column:span 2"><div class="fl">Payment Ref.</div><div class="fv">${receipt.reference}</div></div>` : ""}
        ${receipt.bank ? `<div class="f" style="grid-column:span 2"><div class="fl">Paid Into</div><div class="fv">${receipt.bank}</div></div>` : ""}
        ${receipt.invoice_no ? `<div class="f" style="grid-column:span 2"><div class="fl">Invoice No.</div><div class="fv">${receipt.invoice_no}</div></div>` : ""}
      </div>
    </div>
    <div class="foot"><span>NIQS Nigeria &middot; Official Payment Receipt</span></div>
  </div>
  <script>window.onload=function(){window.print()}</script>
</body>
</html>`);
  w.document.close();
}

// ── Invoice Panel ─────────────────────────────────────────────────────────────
function InvoicePanel({ regId, reg, event }: { regId: string; reg: any; event: any }) {
  const { data: raw, isLoading, isError } = useGetMyEventInvoiceQuery(regId);
  // Only exists once the fee is confirmed — asking earlier just collects a 402.
  const { data: receipt, isFetching: loadingReceipt } = useGetMyEventReceiptQuery(regId, {
    skip: reg?.payment_status !== "paid",
  });

  if (isLoading) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-indigo-400 animate-spin" /></div>;
  if (isError || !raw) return <p className="text-rose-500 text-sm font-medium">Invoice not available.</p>;

  const invoice = readInvoice(raw, reg);
  const isFree  = invoice.status === "free";

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-gray-50 rounded-2xl p-4">
          <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Invoice No</div>
          <div className="font-black text-gray-900 text-sm">{invoice.invoice_no ?? "—"}</div>
        </div>
        <div className="bg-gray-50 rounded-2xl p-4">
          <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">
            {invoice.paid ? "Amount Paid" : "Amount Due"}
          </div>
          <div className="font-black text-gray-900 text-sm">
            {/* A missing amount means we never recorded one, not that the seat
                cost nothing — the panel only renders for events that charge. */}
            {invoice.amount == null || invoice.amount === 0
              ? "—"
              : `₦${invoice.amount.toLocaleString()}`}
          </div>
        </div>
        <div className="bg-gray-50 rounded-2xl p-4">
          <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Status</div>
          <div className={`font-black text-sm capitalize ${
            invoice.paid || isFree ? "text-emerald-700" : "text-amber-700"
          }`}>
            {invoice.status ? (invoice.status === "pending" ? "Not paid" : invoice.status) : "Not paid"}
          </div>
        </div>
        <div className="bg-gray-50 rounded-2xl p-4">
          <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">
            {invoice.paid ? "Paid On" : "Registered"}
          </div>
          <div className="font-black text-gray-900 text-sm">
            {invoice.paid && receipt?.paid_at
              ? fmt(receipt.paid_at)
              : invoice.issued_at ? fmt(invoice.issued_at) : "—"}
          </div>
        </div>

        {/* The receipt number is the payment's, not the invoice's — it only
            exists after confirmation, which is what makes it worth showing. */}
        {receipt?.receipt_number && (
          <div className="bg-emerald-50 rounded-2xl p-4 col-span-2">
            <div className="text-[10px] font-black text-emerald-600/70 uppercase tracking-widest mb-1">Receipt No</div>
            <div className="font-black text-emerald-800 text-sm">{receipt.receipt_number}</div>
          </div>
        )}
      </div>

      {invoice.paid ? (
        <button
          onClick={() => printReceipt(receipt, reg, event)}
          disabled={!receipt || loadingReceipt}
          className="w-full flex items-center justify-center gap-2 bg-gray-900 text-white font-black text-xs py-3 rounded-2xl hover:bg-gray-700 disabled:opacity-50 transition-colors"
        >
          {loadingReceipt
            ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Preparing receipt…</>
            : <><Printer className="w-3.5 h-3.5" /> Print Receipt</>}
        </button>
      ) : !isFree ? (
        <p className="text-xs font-semibold text-amber-700 text-center">
          A receipt is issued once this fee is confirmed.
        </p>
      ) : null}
    </div>
  );
}

// ── Access Card Panel ─────────────────────────────────────────────────────────
function AccessCardPanel({ regId, reg, event, locked }: { regId: string; reg: any; event: any; locked?: boolean }) {
  // The API withholds the card until the fee is settled — don't even ask for it.
  const { data: card, isLoading, isError, refetch } = useGetMyEventAccessCardQuery(regId, { skip: locked });
  const [uploadPhoto, { isLoading: uploading }] = useUploadProfilePhotoMutation();
  const [photoError, setPhotoError] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  // The photograph is the member's profile picture, not something held per
  // event — so uploading it here fixes the card everywhere it appears, and a
  // member whose account has none can still fix theirs before the gate.
  const handlePhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoError("");
    try {
      const fd = new FormData();
      fd.append("pro_pic", file);
      await uploadPhoto(fd).unwrap();
      refetch();
    } catch (err: any) {
      setPhotoError(err?.data?.errors?.pro_pic?.[0] ?? err?.data?.message ?? "Upload failed. Please try again.");
    }
    e.target.value = "";
  };

  if (locked) {
    return (
      <div className="flex flex-col items-center gap-3 py-6 text-center">
        <div className="w-12 h-12 rounded-2xl bg-amber-50 flex items-center justify-center">
          <Lock className="w-5 h-5 text-amber-600" />
        </div>
        <p className="text-sm font-bold text-gray-700">Access card locked</p>
        <p className="text-xs text-gray-400 font-medium max-w-[16rem]">
          Your QR code is issued as soon as your registration fee is confirmed.
        </p>
      </div>
    );
  }

  if (isLoading) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-indigo-400 animate-spin" /></div>;
  if (isError || !card) return <p className="text-rose-500 text-sm font-medium">Access card not available.</p>;

  return (
    <div className="space-y-4">
      {/* The printed card carries the QR that admits its holder, so the settled
          check belongs on the print button too — not only on the panel. A card
          fetched before payment and left open in a tab must not stay printable. */}
      <AccessCardView card={card} event={event} onPrint={isSettled(reg)} />

      {!card.photo_url && (
        <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 space-y-2 text-center">
          <p className="text-xs font-bold text-amber-900">No passport photograph on file</p>
          <p className="text-[11px] font-medium text-amber-800">
            Your card prints without a photo. Upload one so the desk can match it to you at the gate.
          </p>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png" className="hidden" onChange={handlePhoto} />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 bg-amber-600 text-white font-black text-[10px] uppercase tracking-widest px-4 py-2.5 rounded-xl hover:bg-amber-700 disabled:opacity-50 transition-colors"
          >
            {uploading
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</>
              : <><Upload className="w-3.5 h-3.5" /> Upload photo</>}
          </button>
          {photoError && <p className="text-[11px] font-semibold text-rose-700">{photoError}</p>}
        </div>
      )}
    </div>
  );
}

// ── Outstanding fee banner ────────────────────────────────────────────────────
// Lets a member finish paying a registration they left pending (or one recorded
// as pending by the secretariat) without registering all over again.
function PayNowBanner({ regId, amount }: { regId: string; amount: number | null }) {
  const [initiatePayment, { isLoading }] = useInitiateEventPaymentMutation();
  const [error, setError] = useState("");

  const pay = async () => {
    setError("");
    try {
      const res: any = await initiatePayment(regId).unwrap();
      if (res?.already_paid) { window.location.reload(); return; }
      if (!res?.payment_url) throw new Error(res?.message || "Online payment is unavailable right now.");
      submitOnePayForm(res);
    } catch (err: any) {
      setError(err?.data?.message ?? err?.message ?? "Could not start the payment. Please try again.");
    }
  };

  return (
    <div className="bg-amber-50 border border-amber-100 rounded-3xl p-6 space-y-3">
      <div className="flex items-start gap-3">
        <CreditCard className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <div>
          <div className="font-black text-amber-900 text-sm">Payment outstanding</div>
          <p className="text-xs font-medium text-amber-800 mt-0.5">
            Your registration is confirmed once this fee is paid
            {amount != null ? ` — ₦${Number(amount).toLocaleString()}` : ""}.
          </p>
        </div>
      </div>

      {error && <p className="text-xs font-semibold text-rose-700">{error}</p>}

      <button
        onClick={pay}
        disabled={isLoading}
        className="w-full flex items-center justify-center gap-2 bg-gray-900 text-white font-black text-xs py-3 rounded-2xl hover:bg-gray-800 disabled:opacity-50 transition-colors"
      >
        {isLoading
          ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Redirecting to payment…</>
          : <><CreditCard className="w-3.5 h-3.5" /> Pay Now</>}
      </button>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
// ── Event documents ───────────────────────────────────────────────────────────
/**
 * The delegate's copy of the event's files — programme, papers, presentations.
 *
 * Pulled through the authenticated download route rather than linked straight
 * at the storage URL, so the server applies the document's visibility rule and
 * the download is counted. The secretariat's only way of knowing whether the
 * papers were ever read is that tally.
 *
 * A document the delegate may not collect is still listed, greyed, rather than
 * hidden: an empty panel reads as "nothing was published", which is a different
 * and more alarming thing than "this one is not for you".
 */
function EventDocumentsPanel({ eventId }: { eventId: string }) {
  const { data: documents = [], isLoading } = useGetEventDocumentsQuery(eventId);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError]   = useState("");

  const docs = documents as any[];

  const collect = async (d: any) => {
    setBusyId(d.id);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${BASE_URL}/events/${eventId}/documents/${d.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(res.status === 403 ? "That document is not available to you." : `Download failed (${res.status})`);

      const blob = await res.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url;
      a.download = d.file_name ?? d.title;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.message ?? "Could not download that document.");
    }
    setBusyId(null);
  };

  if (isLoading) {
    return <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 text-indigo-400 animate-spin" /></div>;
  }
  if (docs.length === 0) return null;

  return (
    <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 rounded-2xl bg-violet-50 flex items-center justify-center">
          <Paperclip className="w-4 h-4 text-violet-600" />
        </div>
        <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
          Event Documents
        </div>
      </div>

      {error && (
        <div className="mb-3 text-xs font-bold px-3 py-2 rounded-xl bg-rose-50 text-rose-700 border border-rose-100">
          {error}
        </div>
      )}

      <div className="space-y-2">
        {docs.map((d: any) => {
          const allowed = d.can_download !== false;
          return (
            <div
              key={d.id}
              className={`flex items-center gap-3 p-3 rounded-2xl ${allowed ? "bg-gray-50" : "bg-gray-50/60"}`}
            >
              <div className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center text-[9px] font-black uppercase ${allowed ? "bg-white text-gray-500" : "bg-white text-gray-300"}`}>
                {String(d.extension ?? "").slice(0, 4) || <FileText className="w-4 h-4" />}
              </div>

              <div className="flex-1 min-w-0">
                <div className={`font-bold text-sm break-words ${allowed ? "text-gray-800" : "text-gray-400"}`}>
                  {d.title}
                </div>
                {d.description && (
                  <div className="text-[10px] text-gray-400 font-medium break-words">{d.description}</div>
                )}
                {!allowed && (
                  <div className="text-[10px] font-black text-amber-600 uppercase tracking-widest mt-0.5">
                    Delegates only
                  </div>
                )}
              </div>

              {allowed ? (
                <button
                  onClick={() => collect(d)}
                  disabled={busyId === d.id}
                  className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gray-900 text-white text-[10px] font-black uppercase tracking-widest hover:bg-indigo-600 transition-colors disabled:opacity-50"
                >
                  {busyId === d.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                  Download
                </button>
              ) : (
                <Lock className="w-4 h-4 text-gray-300 shrink-0" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function MyEventDetailPage() {
  const { regId } = useParams<{ regId: string }>();
  const navigate  = useNavigate();
  const { data: reg, isLoading, isError } = useGetMyEventRegistrationQuery(regId!);

  if (isLoading) {
    return <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 text-indigo-500 animate-spin" /></div>;
  }

  if (isError || !reg) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-rose-50 border border-rose-100 rounded-3xl p-8 flex items-center gap-4">
          <AlertCircle className="w-6 h-6 text-rose-500 shrink-0" />
          <p className="text-rose-700 font-semibold">Registration not found.</p>
        </div>
      </div>
    );
  }

  const paidUp = isSettled(reg);

  const event: any       = reg.event ?? {};
  // 'absent' is the column's default from the moment of registration, so it is
  // only a fact once the event is behind us. Before that it reads as "not yet".
  const overall           = attendanceDisplay(reg.overall_attendance, event);
  const attendance: any[] = reg.attendance ?? [];
  const materials: any[]  = reg.materials ?? [];
  const refreshments: any[] = reg.refreshment_choices ?? [];

  // What was actually bought, as the member's own record of it: the amount, the
  // category it was charged under, the seat or the stream, and which of the
  // event's three fees applied. All four now ride on the registration, which is
  // the only place they survive the fee list being rewritten.
  // Label and figure from one derivation — worked out separately they could
  // contradict each other ("Amount due: Free" on a settled registration whose
  // amount was never recorded).
  const fee = registrationFee(reg, event);
  const summary = [
    // Omitted entirely on an event with no fee — there is nothing to report.
    fee ? { label: fee.label, value: fee.value, strong: true } : null,
    reg.category_label ? { label: "Category", value: reg.category_label } : null,
    reg.attendance_mode === "virtual" ? { label: "Attending", value: "Virtual" } : null,
    reg.price_window_label ? { label: "Rate", value: `${reg.price_window_label} rate` } : null,
    reg.registered_at ? { label: "Registered", value: fmt(reg.registered_at) } : null,
  ].filter(Boolean) as { label: string; value: string; strong?: boolean }[];

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 pb-20 space-y-6">

      {/* Back */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm font-bold text-gray-500 hover:text-gray-800 transition-colors"
      >
        <ChevronLeft className="w-4 h-4" /> All my events
      </button>

      {/* Banner */}
      <div className="rounded-3xl overflow-hidden border border-gray-100 shadow-sm">
        {event.background_image ? (
          <img src={event.background_image} alt="" className="w-full h-48 md:h-64 object-cover" />
        ) : (
          <div className="w-full h-48 md:h-64 bg-gradient-to-br from-gray-900 via-indigo-900 to-indigo-700 flex items-center justify-center">
            <CalendarCheck className="w-16 h-16 text-white/20" />
          </div>
        )}
      </div>

      {/* ── Title beside the registration summary ──────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">

        <div className="lg:col-span-3">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className={`inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-xl border ${
              paidUp
                ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                : "bg-amber-50 text-amber-700 border-amber-100"
            }`}>
              {paidUp ? <CheckCircle2 className="w-3 h-3" /> : <CreditCard className="w-3 h-3" />}
              {/* Driven by the fee derivation, not payment_status: a CPD or
                  legacy back-fill carries `free` on an event that charges, and
                  this badge must not disagree with the figure beside it. */}
              {paidUp ? (fee?.tone === "settled" ? "Paid" : "Registered") : "Fee outstanding"}
            </span>
            <span className={`text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-xl border ${ATTENDANCE_TONE_CLASSES[overall.tone]}`}>
              {overall.label}
            </span>
            {reg.attendance_mode === "virtual" && (
              <span className="text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-xl bg-sky-50 text-sky-700 border border-sky-100">
                Virtual
              </span>
            )}
            {reg.cpd_awarded && (
              <span className="text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-xl bg-violet-50 text-violet-700 border border-violet-100">
                CPD awarded
              </span>
            )}
          </div>

          <h1 className="text-3xl md:text-4xl font-black text-gray-900 leading-tight tracking-tight uppercase">
            {event.title ?? "Event"}
          </h1>

          <div className="inline-flex items-center gap-2 mt-3 bg-gray-900 text-white rounded-2xl px-4 py-2">
            <span className="text-[9px] font-black uppercase tracking-widest text-white/50">Reg No.</span>
            <span className="font-black text-sm font-mono">{reg.registration_no}</span>
          </div>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-6 pt-6 border-t border-gray-100">
            {event.start_date && (
              <div className="flex items-center gap-2 text-sm text-gray-600 font-semibold">
                <CalendarCheck className="w-4 h-4 text-amber-500 shrink-0" />
                {fmt(event.start_date)}
                {event.end_date && event.end_date !== event.start_date && ` – ${fmt(event.end_date)}`}
              </div>
            )}
            {event.location && (
              <div className="flex items-center gap-2 text-sm text-gray-600 font-semibold">
                <MapPin className="w-4 h-4 text-amber-500 shrink-0" />
                {event.location}
              </div>
            )}
          </div>
        </div>

        {/* ── Registration summary ─────────────────────────────────────── */}
        <div className="lg:col-span-2 space-y-4 lg:sticky lg:top-6">
          <div className="bg-white border border-gray-100 rounded-3xl shadow-lg shadow-gray-100/70 p-6 space-y-4">
            <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Your registration</div>

            {summary.map((row) => (
              <div key={row.label} className="flex items-baseline justify-between gap-3 border-b border-gray-50 last:border-0 pb-3 last:pb-0">
                <span className="text-sm font-medium text-gray-500 shrink-0">{row.label}</span>
                <span className={row.strong
                  ? "text-2xl font-black text-gray-900 text-right"
                  : "text-sm font-bold text-gray-800 text-right"}>
                  {row.value}
                </span>
              </div>
            ))}
          </div>

          {/* Any unsettled fee on a paid event can still be completed here. */}
          {!paidUp && event.is_paid !== false && (
            <PayNowBanner regId={regId!} amount={reg.amount_paid ?? null} />
          )}
        </div>
      </div>

      {/* Invoice + Access Card. An event with no fee raises no invoice — no
          number, no amount, no receipt — so the panel is dropped rather than
          shown full of dashes, and the access card takes the width. */}
      <div className={`grid grid-cols-1 gap-6 ${fee ? "md:grid-cols-2" : ""}`}>
        {fee && (
          <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-2xl bg-amber-50 flex items-center justify-center">
                <FileText className="w-4 h-4 text-amber-600" />
              </div>
              <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Invoice</div>
            </div>
            <InvoicePanel regId={regId!} reg={reg} event={event} />
          </div>
        )}

        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 rounded-2xl bg-indigo-50 flex items-center justify-center">
              <QrCode className="w-4 h-4 text-indigo-600" />
            </div>
            <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Access Card</div>
          </div>
          {/* Locked for anything that isn't settled — "not pending" was too
              loose: a failed or missing status left the card unlocked. */}
          <AccessCardPanel regId={regId!} reg={reg} event={event} locked={!paidUp} />
        </div>
      </div>

      {/* Attendance per day */}
      {attendance.length > 0 && (
        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-2xl bg-emerald-50 flex items-center justify-center">
              <CalendarCheck className="w-4 h-4 text-emerald-600" />
            </div>
            <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Attendance Record</div>
          </div>
          <div className="space-y-2">
            {attendance.map((a: any) => {
              // A day still to come is not a day you missed.
              const day = dayAttendanceDisplay(!!a.is_present, a.day_date);
              return (
                // The payload carries no row id — day_id is what identifies it.
                <div key={a.day_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-2xl">
                  <div>
                    <div className="font-bold text-gray-800 text-sm">{a.day_label}</div>
                    <div className="text-xs text-gray-400 font-medium">{a.day_date ? fmt(a.day_date) : ""}</div>
                  </div>
                  <span className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-xl border flex items-center gap-1 ${ATTENDANCE_TONE_CLASSES[day.tone]}`}>
                    {day.tone === "present" && <CheckCircle2 className="w-3 h-3" />}
                    {day.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* The event's files. Renders nothing until something is published, so
          it costs an empty card on events that never put papers up. */}
      {event.id && <EventDocumentsPanel eventId={String(event.id)} />}

      {/* Materials */}
      {materials.length > 0 && (
        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-2xl bg-amber-50 flex items-center justify-center">
              <Package className="w-4 h-4 text-amber-600" />
            </div>
            <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Materials</div>
          </div>
          <div className="space-y-2">
            {materials.map((m: any) => (
              <div key={m.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-2xl">
                <div className="font-bold text-gray-800 text-sm">{m.name}</div>
                {m.is_collected ? (
                  <span className="text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-100">Collected</span>
                ) : (
                  <span className="text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-xl bg-gray-100 text-gray-400 border border-gray-100">Pending</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Refreshments */}
      {refreshments.length > 0 && (
        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-2xl bg-rose-50 flex items-center justify-center">
              <Coffee className="w-4 h-4 text-rose-600" />
            </div>
            <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
              Refreshments · {reg.refreshment_mode === "takeaway" ? "Takeaway" : "Buffet"}
            </div>
          </div>
          <div className="space-y-2">
            {refreshments.map((c: any) => (
              <div key={c.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-2xl">
                <div>
                  <div className="font-bold text-gray-800 text-sm">{c.refreshment_name}</div>
                  <div className="text-xs text-gray-400 font-medium">{c.day_label}</div>
                </div>
                {c.is_collected ? (
                  <span className="text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-100">Collected</span>
                ) : (
                  <span className="text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-xl bg-gray-100 text-gray-400 border border-gray-100">Pending</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
